import { ref, getBlob } from 'firebase/storage';
import { storage } from '../services/startStorage';
import { userTempInventoryState, type StoredTempAttachment } from './userTempInventoryState';
import { listUserTempFiles, type UserTempFileMeta } from './listUserTempFiles';
import type { ParsedEmailSidecar } from './writeParsedEmailSidecar';

const EMAIL_CONTENT_TYPES = new Set(['message/rfc822', 'application/vnd.ms-outlook']);

let inFlight: Promise<void> | null = null;

function classify(contentType: string): 'image' | 'email' | 'tabular' {
    if (contentType.startsWith('image/')) return 'image';
    if (EMAIL_CONTENT_TYPES.has(contentType)) return 'email';
    return 'tabular';
}

async function loadEmailSidecar(fileFolder: string): Promise<ParsedEmailSidecar | null> {
    try {
        const sidecarRef = ref(storage, `${fileFolder}/parsedEmail.json`);
        const blob = await getBlob(sidecarRef);
        const text = await blob.text();
        return JSON.parse(text) as ParsedEmailSidecar;
    } catch {
        return null;
    }
}

async function toAttachment(meta: UserTempFileMeta): Promise<StoredTempAttachment> {
    const contentType = meta.contentType ?? 'application/octet-stream';
    const kind = classify(contentType);
    if (kind === 'email') {
        const parsed = await loadEmailSidecar(meta.fileFolder);
        return {
            kind: 'email',
            filename: meta.filename,
            fileId: meta.fileId,
            fileFolder: meta.fileFolder,
            originalPath: meta.originalPath,
            contentType,
            sizeBytes: meta.sizeBytes,
            subject: parsed?.subject ?? meta.filename,
            from: parsed?.from,
            body: parsed?.body ?? '',
        };
    }
    return {
        kind,
        filename: meta.filename,
        fileId: meta.fileId,
        fileFolder: meta.fileFolder,
        originalPath: meta.originalPath,
        contentType,
        sizeBytes: meta.sizeBytes,
    };
}

export function refreshUserTempInventory(): Promise<void> {
    if (inFlight) return inFlight;
    const userId = userTempInventoryState.userId;
    if (!userId) return Promise.resolve();

    userTempInventoryState.loading = true;
    inFlight = (async () => {
        try {
            const list = await listUserTempFiles(userId);
            if (userTempInventoryState.userId !== userId) return;
            const files = await Promise.all(list.map(toAttachment));
            if (userTempInventoryState.userId !== userId) return;
            userTempInventoryState.files = files;
            userTempInventoryState.ready = true;
        } finally {
            userTempInventoryState.loading = false;
            inFlight = null;
        }
    })();
    return inFlight;
}
